import { useCallback, useState } from 'react';

/**
 * Owns "am I editing an already-scheduled email, and which one".
 *
 * Deliberately holds only the edit-mode bookkeeping. Loading a scheduled email touches
 * the body, attachments, From address and schedule date, so that orchestration stays in
 * the composer where all those hooks are in scope — hiding it in here would just move
 * the coupling somewhere less obvious.
 */
export interface UseScheduledEmailEditResult {
  editMode: boolean;
  /** ScheduleID of the row being edited. */
  editingScheduleId: number | null;
  /** Same value; kept separate because attachment flows historically re-pointed it. */
  currentScheduleId: number | null;
  /** True when the user chose "Send Now" on a scheduled email. */
  sendNowMode: boolean;
  setSendNowMode: (value: boolean) => void;
  /** Enter edit mode for a given schedule. */
  beginEdit: (scheduleId: number) => void;
  reset: () => void;
}

export function useScheduledEmailEdit(): UseScheduledEmailEditResult {
  const [editMode, setEditMode] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState<number | null>(null);
  const [currentScheduleId, setCurrentScheduleId] = useState<number | null>(null);
  const [sendNowMode, setSendNowMode] = useState(false);

  const beginEdit = useCallback((scheduleId: number) => {
    setEditMode(true);
    setEditingScheduleId(scheduleId);
    setCurrentScheduleId(scheduleId);
    setSendNowMode(false);
  }, []);

  const reset = useCallback(() => {
    setEditMode(false);
    setEditingScheduleId(null);
    setCurrentScheduleId(null);
    setSendNowMode(false);
  }, []);

  return {
    editMode,
    editingScheduleId,
    currentScheduleId,
    sendNowMode,
    setSendNowMode,
    beginEdit,
    reset,
  };
}

export default useScheduledEmailEdit;
